import LoaderIcon from "@assessment/components/atoms/loaderIcon";
import {
  useGetSaveSubjectiveQLQuery,
  useSaveSubjectiveMutation,
  useResetSubjectiveQuestionMutation
} from "@assessment/store/slices/api/assessmentApiSlice";
import { AssessmentSliceActions } from "@assessment/store/slices/assessmentSlice";
import { useAppDispatch, useAppSelector } from "@assessment/store/store";
import Button from "@lxp-app/components/atoms/button";
import { InfoComponent } from "@lxp-app/components/atoms/infoComponent";
import RichTextEditor from "@lxp-app/components/molecules/rich-text-editor/richTextEditor";
import GetHeroIcon from "@lxp-app/utils/getHeroIcon";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useShowErrors } from "../../../toasterWrapper";
import ContentDeleteModal from "@lxp-app/components/molecules/modal-children/contentDeleteModal";
import Modal from "@lxp-app/components/atoms/modal";
import { createTextFile, extractPlainText } from "../../../saveAnswers";

interface TextRes {
  question: any;
  uploadConfig: any;
  sectionId: string;
  latestSubmissionId: string;
  refetchSavedAnswers: () => void;
}

const TextResponse = ({
  question,
  uploadConfig,
  sectionId,
  latestSubmissionId,
  refetchSavedAnswers
}: TextRes) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const showErrors = useShowErrors();

  const [text, setText] = useState<string>("");
  // const [isSaving, setIsSaving] = useState(false);
  const [qnStatus, setQnStatus] = useState<string>("");
  const [modalOpen, setModalOpen] = useState(false);
  const [isChanged, setIsChanged] = useState(false);

  const [saveSubjectiveApi] = useSaveSubjectiveMutation();
  const [resetSubjectiveQuestionQuery] = useResetSubjectiveQuestionMutation();

  const textRef = useRef(text);
  const questionRef = useRef(question);

  const activeQuestionMeta = useAppSelector(
    (store) => store.AssessmentSlice.activeQuestionMeta
  );
  const ongoingId = useAppSelector((store) => store.AssessmentSlice.ongoingId);
  const constants = useAppSelector((store) => store.AssessmentSlice.duration);
  const isSaving = useAppSelector((store) => store.AssessmentSlice.isSaving);
  const assessmentProgress = useAppSelector(
    (store) => store.AssessmentSlice.assessmentProgress
  );

  const { data, isLoading } = useGetSaveSubjectiveQLQuery(
    {
      ongoingId: ongoingId,
      questionId: question?.questionId,
      questionVersion: question?.versionnumber
    },
    {
      refetchOnMountOrArgChange: true
    }
  );
  const savedAnswers: any =
    data?.getSaveSubjectiveQL?.data?.savedSubjective?.[0]?.usersubmission;

  const [autoSave, setAutoSave] = useState(false);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isChangedRef = useRef(isChanged);
  const textValueRef = useRef(text);
  const lastSavedTextRef = useRef("");

  const getWordCount = (val: string) => {
    return extractPlainText(val).trim().split(/\s+/).filter(Boolean).length;
  };
  const autoSaveApi = useCallback(async () => {
    const words = getWordCount(textValueRef.current);

    if (words < 40 || textValueRef.current === lastSavedTextRef.current) return;

    try {
      setAutoSave(true);

      const res: any = await saveSubjectiveApi({
        ongoingId: ongoingId ?? "",
        questionId: questionRef.current?.questionId,
        quesVersion: questionRef.current?.versionnumber ?? 1,
        body: {
          solution: btoa(unescape(encodeURIComponent(textValueRef.current))),
          uploadConfigId: uploadConfig?.id,
          fileDetails: {
            fileName: "subjective",
            fileSize: 10,
            fileExtension: ".txt",
            filePath: "string"
          }
        }
      });

      if (res?.data?.data?.uploadUrlDetails?.data) {
        const uploadConfigData = res?.data?.data?.uploadUrlDetails?.data;
        const textFile = createTextFile(textValueRef.current);

        if (textFile) {
          await uploadFileToSignedUrl(
            uploadConfigData?.upload_url,
            textFile,
            uploadConfigData?.submission_uuid,
            true
          );
          lastSavedTextRef.current = textValueRef.current;
        }
      }
    } catch (err) {
      console.log("Auto save failed", err);
    } finally {
      setAutoSave(false);
    }
  }, [ongoingId, uploadConfig, saveSubjectiveApi]);
  useEffect(() => {
    if (savedAnswers) setText(savedAnswers);
    else setText("");
  }, [savedAnswers, question?.questionId]);

  useEffect(() => {
    textRef.current = text;
  }, [text]);

  useEffect(() => {
    isChangedRef.current = isChanged;
  }, [isChanged]);

  useEffect(() => {
    textValueRef.current = text;
  }, [text]);

  useEffect(() => {
    if (assessmentProgress?.[sectionId]) {
      const qnList = assessmentProgress?.[sectionId]?.questions;
      if (question?.questionId in qnList) {
        if (qnList?.[question?.questionId]?.status)
          setQnStatus(qnList?.[question?.questionId]?.status);
      }
    }
  }, [question?.questionId]);

  useEffect(() => {
    setIsChanged(false);
    dispatch(AssessmentSliceActions.mutateSaving(false));
    questionRef.current = question;
  }, [question?.questionId]);

  const saveAndComplete = async () => {
    try {
      dispatch(AssessmentSliceActions.mutateSaving(true));
      const newTxt = textRef.current;
      const qnRef = questionRef.current;
      const res: any = await saveSubjectiveApi({
        ongoingId: ongoingId ?? "",
        questionId: qnRef?.questionId,
        quesVersion: qnRef?.versionnumber ?? 1,
        body: {
          solution: btoa(unescape(encodeURIComponent(newTxt))),
          uploadConfigId: uploadConfig?.id,
          fileDetails: {
            fileName: "subjective",
            fileSize: 10,
            fileExtension: ".txt",
            filePath: "string"
          }
        }
      });
      if (res?.data?.data?.uploadUrlDetails?.data) {
        const uploadConfig = res?.data?.data?.uploadUrlDetails?.data;
        const textFile = createTextFile(text);
        dispatch(
          AssessmentSliceActions.mutateCandidateResponse({
            questionId: question?.questionId,
            answerKey: []
          })
        );
        if (textFile) {
          uploadFileToSignedUrl(
            uploadConfig?.upload_url,
            textFile,
            uploadConfig?.submission_uuid
          );
          refetchSavedAnswers();
        }
      } else {
        const rawErrList = res?.error?.data?.error;
        const errList =
          rawErrList?.length > 0
            ? rawErrList?.map((el: string) => el.split("_")[0])
            : [];
        setIsChanged(true);
        dispatch(AssessmentSliceActions.mutateSaving(false));
        if (showErrors) showErrors("Submission Failed", errList, "error", "");
      }
    } catch (error: any) {
      setIsChanged(true);
      dispatch(AssessmentSliceActions.mutateSaving(false));
      if (showErrors)
        showErrors(
          "Submission Failed",
          [],
          "error",
          "Failed to submit the response. Please try again later!"
        );
    }
  };
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      autoSaveApi();
    }, 60000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [autoSaveApi]);
  const uploadFileToSignedUrl = async (
    signedUrl: string,
    file: File,
    submissionUuid: string,
    isAutoSave = false
  ) => {
    try {
      const res = await fetch(signedUrl, {
        method: "PUT",
        headers: {
          "Content-Type": "text/plain",
          "x-goog-meta-submission-uuid": submissionUuid
        },
        body: file
      });
      if (!res.ok) {
        dispatch(AssessmentSliceActions.mutateSaving(false));
        if (showErrors)
          showErrors(
            "Submission Failed",
            [],
            "error",
            "Failed to submit the response. Please try again later!"
          );
        return;
      }

      if (showErrors)
        showErrors(
          isAutoSave ? "Response auto-saved" : "Response Submitted",
          [],
          "success",
          isAutoSave
            ? "Response auto-saved successfully."
            : "Successfully submitted the response."
        );
      dispatch(
        AssessmentSliceActions.mutateQuestionProgress({
          questionId: question.questionId,
          sectionId: sectionId,
          status: "completed"
        })
      );
      dispatch(AssessmentSliceActions.mutateSaving(false));
      setIsChanged(false);
      dispatch(
        AssessmentSliceActions.mutateCandidateResponse({
          questionId: question?.questionId,
          answerKey: []
        })
      );
      return res;
    } catch (error: any) {
      dispatch(AssessmentSliceActions.mutateSaving(false));
      setIsChanged(true);
      if (showErrors)
        showErrors(
          "Submission Failed",
          [],
          "error",
          "Failed to submit the response. Please try again later!"
        );
    }
  };

  const handleModal = (): void => {
    setModalOpen(!modalOpen);
  };

  const textChange = (value: any) => {
    setText(value);
    const hasTyped = extractPlainText(value).trim().length > 0;
    if (!hasTyped) {
      setIsChanged(false);
      dispatch(
        AssessmentSliceActions.mutateCandidateResponse({
          questionId: question?.questionId,
          answerKey: []
        })
      );
      return;
    }
    if ((savedAnswers && savedAnswers != value) || !savedAnswers) {
      setIsChanged(true);
      dispatch(
        AssessmentSliceActions.mutateCandidateResponse({
          questionId: question?.questionId,
          answerKey: {
            value: value,
            version: question?.versionnumber,
            uploadConfigId: uploadConfig?.id
          }
        })
      );
    }
  };
  const wordCount = extractPlainText(text)
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;

  const checkDisable = () => {
    return (
      extractPlainText(text).trim().split(/\s+/).filter(Boolean).length < 40
    );
  };

  const restToEmptyText = async () => {
    if (latestSubmissionId) {
      try {
        const res = await resetSubjectiveQuestionQuery({
          ongoingId: ongoingId ?? "",
          questionId: question?.questionId,
          questionVersion: question?.versionnumber,
          latestSubmissionId: latestSubmissionId
        });
        if (res?.data?.data?.message) {
          dispatch(
            AssessmentSliceActions.mutateQuestionProgress({
              questionId: question.questionId,
              sectionId: question?.sectionId,
              status: "unanswered"
            })
          );
        }
      } catch (error) {
        console.error("Error resetting subjective question:", error);
      }
    }
    setText("");
  };

  return (
    <div className="h-full flex flex-col px-2 pt-2">
      <div className="mb-2">
        <InfoComponent
          textSize={"sm"}
          iconSize={4}
          iconVariant={"outline"}
          text={t("assessment.subjective.minWordsRequired")}
          iconPosition={"start"}
        />
      </div>

      <div className="flex-1 overflow-auto">
        <RichTextEditor
          name="longDescription"
          placeholder=""
          optional={true}
          value={text}
          subjective={true}
          setValue={(value: any) => {
            textChange(value);
          }}
          infoMessage={t("assessment.subjective.enterYourAnswer")}
          testId="subjectiveQnAns"
          onButtonClick={(e: any) => {
            e.preventDefault();
          }}
        />
      </div>

      <div className="sticky bottom-0 pt-4 pb-10 gap-4 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-gray-500 min-h-[20px]">
          {wordCount >= 40 &&
            (autoSave ? (
              <>
                <LoaderIcon />
                <span>{t("assessment.coding.saving")}</span>
              </>
            ) : (
              <div>
                <span>{t("assessment.coding.autoSaveEnabled")}</span>
              </div>
            ))}
        </div>

        <div className="flex gap-4">
          <Button
            buttonColor="secondary"
            variant="outlined"
            onClick={() => handleModal()}
            disable={text?.length == 0}
          >
            {t("assessment.coding.reset")}
          </Button>

          <Button
            variant="contained"
            label={t("assessment.subjective.submitResponse")}
            onClick={saveAndComplete}
            disable={checkDisable()}
            buttonColor="primary"
            isLoading={isSaving}
            loadingLabel={t("assessment.subjective.submittingResponse")}
          />
        </div>
      </div>

      <Modal
        open={modalOpen}
        setOpen={handleModal}
        dialogueClassName="sm:max-w-3xl"
        children={
          <ContentDeleteModal
            isShow={modalOpen}
            show={() => {
              setText("");
              setIsChanged(false);
              restToEmptyText();
              handleModal();
            }}
            setOpen={handleModal}
            buttonName={t("assessment.coding.reset")}
            heading={t("assessment.subjective.resetAnswer")}
            commentsAllowed={false}
            msg={t("assessment.subjective.resetAnswerMessage")}
          />
        }
      />
    </div>
  );
};

export default TextResponse;
